import { Download, FileQuestion } from 'lucide-react';

import { PermissionGate } from '../../../../auth/PermissionGate';
import { Button } from '../../../../components/Button';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import { Skeleton } from '../../../../components/Skeleton';
import { useDocumentPreview } from '../useDocumentPreview';
import type { RepositoryItemListItem } from '../repositoryTypes';

/**
 * A document's stored file, shown in place.
 *
 * **It renders only what can be rendered INERTLY**, and that is the whole design rather than a
 * limitation — see `useDocumentPreview`, which owns the reasoning and the allow-list. An image
 * goes in an `<img>`, which executes nothing whatever the bytes turn out to be; a PDF goes to
 * the browser's own sandboxed viewer through a blob that has been re-typed to
 * `application/pdf`. Everything else says it cannot be shown and offers the download.
 *
 * **Four states, and all four render.** Loading, a file that cannot be previewed, a record with
 * no file at all, and the preview itself — plus the failure, which is its own. A panel that
 * only handled the happy path would leave a reader staring at nothing on every other one.
 *
 * **It is mounted only while its tab is open**, which is what makes the fetch lazy — see
 * `useDocumentPreview`. Rendering it behind a hidden tab would pull a 50 MB file down for a
 * reader who never asked for it.
 *
 * **The panel is `bleed`**, so the viewer gets the whole height: a PDF in a card capped to a
 * readable measure is a page of document behind a letterbox.
 */
export function DocumentPreview({
  document,
  onDownload,
}: {
  document: RepositoryItemListItem;
  onDownload: () => void;
}) {
  const { kind, url, loading, error } = useDocumentPreview(document.id, document.fileContentType);

  /*
    FULL WIDTH, inside a bounded column.

    This is the only thing a reader can do on a panel that cannot show them what they came
    for, so it takes the whole width of the block rather than sitting under the message as a
    chip — and the preview is the tab the profile LANDS on, so it is often the first control
    on screen.

    The cap is what stops "full width" meaning the width of a bleed panel: the viewer gets the
    whole height and, on a wide monitor, a button spanning 1400px under a centred sentence
    reads as a banner rather than a button. `max-w-sm` is the measure the message beside it
    already occupies.
  */
  const download = (
    <PermissionGate permission="repository.download">
      <div className="w-full max-w-sm">
        <Button
          id="repository-document-preview-download"
          variant="secondary"
          fullWidth
          leading={<Download className="h-4 w-4" aria-hidden="true" />}
          onClick={onDownload}
        >
          Download
        </Button>
      </div>
    </PermissionGate>
  );

  /*
    NO FILE is a different fact from "cannot be previewed", and the reader is looking for
    different things in each. A row from before uploads created the record and its bytes
    together has nothing to show and nothing to download.
  */
  if (document.storedFileId === null) {
    return (
      <div className="flex h-full min-h-0 flex-col items-center justify-center">
        <EmptyState message="This document has no file, so there is nothing to preview. It was registered before uploads created the record and its file together, and supplying one is a new version." />
      </div>
    );
  }

  if (kind === 'none') {
    return (
      <div className="flex h-full min-h-0 flex-col items-center justify-center gap-4">
        <FileQuestion className="h-10 w-10 text-slate-300" aria-hidden="true" />
        <EmptyState
          minHeight="min-h-0"
          message={
            document.fileContentType
              ? `This file is ${document.fileContentType}, which cannot be shown here. Download it to open it in the application it belongs to.`
              : 'This file cannot be shown here. Download it to open it in the application it belongs to.'
          }
        />
        {download}
      </div>
    );
  }

  if (loading) {
    // The shape of what it replaces — one tall block, because that is what a page of document
    // looks like. A stack of small ones would read as a list arriving.
    return <Skeleton className="h-full min-h-[420px] w-full" />;
  }

  /*
    A FAILED preview is not a failed page. The record is still on screen above it and is still
    the last good answer, so this reports inside the panel and offers the way out that always
    works — the download, which goes through a different path.
  */
  if (error || !url) {
    return (
      <div className="flex h-full min-h-0 flex-col items-center justify-center gap-4">
        <ErrorState message={error?.message ?? 'Could not load the preview.'} />
        {download}
      </div>
    );
  }

  if (kind === 'image') {
    return (
      <div className="modern-scrollbar flex h-full min-h-0 items-start justify-center overflow-auto bg-slate-50 p-4">
        {/*
          The uploaded name is what this image IS, as far as the system knows — so it is the
          alt text rather than a coined description. An empty alt would make the panel's only
          content invisible to a reader who cannot see it, which is the opposite of the
          decorative case.
        */}
        <img
          src={url}
          alt={document.fileOriginalName ?? document.name}
          className="max-w-full rounded-lg border border-slate-200 bg-white shadow-sm"
        />
      </div>
    );
  }

  return (
    /*
      An `<iframe>` rather than an `<object>`: it is the element browsers agree to route a
      blob URL through to their built-in PDF viewer, and the one whose failure mode is a
      blank frame rather than a download prompt.

      The blob is already pinned to `application/pdf` by the hook, which is what makes this
      safe to point at content the server holds on somebody's behalf.
    */
    <iframe
      src={url}
      title={`Preview of ${document.name}`}
      className="h-full min-h-[420px] w-full border-0 bg-slate-50"
    />
  );
}
